import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus } from 'lucide-react';
import { useCart } from '../context/CartContext';

const Cart = () => {
  const { cart, removeFromCart, updateQuantity, cartTotal, cartCount } = useCart();
  const [couponCode, setCouponCode] = useState('');
  const [discount, setDiscount] = useState(0);
  const navigate = useNavigate();

  const applyCoupon = () => {
    if (couponCode.toUpperCase() === 'SIYAA10') {
      setDiscount(cartTotal * 0.1);
    } else if (couponCode) {
      alert('Invalid coupon code');
    }
  };

  const shippingCharge = 100;
const finalTotal = cartTotal - discount + shippingCharge;

  if (cart.length === 0) {
    return (
      <div className="bg-cream min-h-screen">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 text-center">
          <h1 className="font-serif text-4xl md:text-5xl text-charcoal mb-6" data-testid="empty-cart-title">
            Your Cart is Empty
          </h1>
          <p className="text-[#6B665F] font-light text-lg mb-8">
            Discover our collection of ethical luxury
          </p>
          <Link
            to="/shop"
            className="inline-flex items-center justify-center bg-black text-white px-8 py-3 rounded-lg shadow-md hover:bg-gray-900 hover:shadow-lg transition-all duration-300 text-sm tracking-wide font-medium"
              data-testid="hero-cta-button"
          >
            Continue Shopping
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-cream min-h-screen">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <h1 className="font-serif text-4xl md:text-5xl text-charcoal mb-12" data-testid="cart-page-title">
          Shopping Cart
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-6" data-testid="cart-items">
            {cart.map((item) => (
              <div
                key={`${item.id}-${item.size}`}
                className="bg-white p-6 border border-[#E5E0D8] flex gap-6"
                data-testid={`cart-item-${item.id}`}
              >
                <img
                  src={item.images[0]}
                  alt={item.name}
                  className="w-24 h-32 object-cover"
                />
                <div className="flex-1 space-y-3">
                  <div>
                    <h3 className="font-serif text-lg text-charcoal">{item.name}</h3>
                    <p className="text-xs uppercase tracking-wider text-[#6B665F]">
                      Size: {item.size}
                    </p>
                  </div>

                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => updateQuantity(item.id, item.size, item.quantity - 1)}
                      className="w-8 h-8 border border-[#E5E0D8] rounded-full hover:border-bronze transition-colors flex items-center justify-center"
                      data-testid={`decrease-quantity-${item.id}`}
                    >
                      <Minus size={14} />
                    </button>
                    <span className="text-sm font-medium w-8 text-center" data-testid={`item-quantity-${item.id}`}>
                      {item.quantity}
                    </span>
                    <button
                      onClick={() => updateQuantity(item.id, item.size, item.quantity + 1)}
                      className="w-8 h-8 border border-[#E5E0D8] rounded-full hover:border-bronze transition-colors flex items-center justify-center"
                      data-testid={`increase-quantity-${item.id}`}
                    >
                      <Plus size={14} />
                    </button>
                  </div>

                  <div className="flex items-center justify-between">
                    <p className="text-bronze font-medium" data-testid={`item-price-${item.id}`}>
                      ₹{(item.price * item.quantity).toLocaleString()}
                    </p>
                    <button
                      onClick={() => removeFromCart(item.id, item.size)}
                      className="text-[#6B665F] hover:text-red-500 transition-colors"
                      data-testid={`remove-item-${item.id}`}
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white p-8 border border-[#E5E0D8] sticky top-24" data-testid="order-summary">
              <h2 className="font-serif text-2xl text-charcoal mb-6">Order Summary</h2>

              <div className="space-y-4 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-[#6B665F]">Subtotal ({cartCount} items)</span>
                  <span className="text-charcoal font-medium" data-testid="subtotal">
                    ₹{cartTotal.toLocaleString()}
                  </span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Discount</span>
                    <span data-testid="discount-amount">-₹{discount.toLocaleString()}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-[#6B665F]">Shipping</span>
                  <span className="text-charcoal font-medium">₹100</span>
                </div>
                  </div>

              <div className="h-px bg-[#E5E0D8] my-6"></div>

              <div className="flex justify-between mb-6">
                <span className="font-serif text-lg text-charcoal">Total</span>
                <span className="font-serif text-xl text-bronze" data-testid="cart-total">
                  ₹{finalTotal.toLocaleString()}
                </span>
              </div>

              {/* Coupon Code */}
              <div className="mb-6">
                <label className="text-xs uppercase tracking-wider text-charcoal mb-2 block">
                  Coupon Code
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                    placeholder="Enter code"
                    className="flex-1 bg-transparent border-b border-[#E5E0D8] px-0 py-2 focus:border-bronze focus:outline-none text-sm"
                    data-testid="coupon-input"
                  />
                  <button
                    onClick={applyCoupon}
                    className="text-bronze text-sm uppercase tracking-wider hover:text-bronze-dark"
                    data-testid="apply-coupon-button"
                  >
                    Apply
                  </button>
                </div>
              </div>

              <button
                onClick={() => navigate('/checkout')}
                className="w-full inline-flex items-center justify-center bg-black text-white px-8 py-3 rounded-lg shadow-md hover:bg-gray-900 hover:shadow-lg transition-all duration-300 text-sm tracking-wide font-medium"
              >
                Proceed to Checkout
              </button>

              <Link
                to="/shop"
                className="w-full inline-flex items-center justify-center border border-black text-black px-8 py-3 rounded-lg hover:bg-black hover:text-white transition-all duration-300 text-sm tracking-wide font-medium mt-3"
              >
                Continue Shopping
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
